import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'
import { useInvalidateProjectNewsCache } from './useProjectNews'

const NEWS_PIECE_CACHE = jotai.atom<hooks.Cache<api.News>>({})

type NewsPieceConfig = {
  fetchOnMount?: boolean | 'force'
  withToken?: boolean
}

export function useNewsPiece(
  projectId: number,
  newsPieceId?: number,
  { fetchOnMount = true, withToken = false }: NewsPieceConfig = { fetchOnMount: true, withToken: false },
) {
  const {
    getCurrentResponse,
    query: queryNewsPiece,
    patch,
  } = hooks.useSharedQuery(
    (newsPieceId: number, withToken) => {
      const news = base.get(newsPieceId, withToken)
      invalidateProjectNewsCache()
      return news
    },
    {
      atom: NEWS_PIECE_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(newsPieceId, withToken))

  const invalidateProjectNewsCache = useInvalidateProjectNewsCache(projectId)

  return { response: getResponse(), query, createDraft, publishDraft, unpublishPost, updatePost, deletePost }

  async function createDraft(payload: api.CreateNews): Promise<api.HttpResult<api.News> | undefined> {
    const createDraftResponse = await base.createDraft(projectId, payload)

    return createDraftResponse.next((successCreateDraft) => {
      invalidateProjectNewsCache()

      return successCreateDraft
    })
  }

  async function publishDraft(): Promise<api.HttpResult<api.News> | undefined> {
    if (newsPieceId === undefined) {
      return
    }

    return patch([newsPieceId, withToken], async (response) => {
      return (await base.publishDraft(newsPieceId)).next((successPublishPost) => {
        invalidateProjectNewsCache()

        return successPublishPost
      })
    })
  }

  async function unpublishPost(): Promise<api.HttpResult<api.News> | undefined> {
    if (newsPieceId === undefined) {
      return
    }

    return patch([newsPieceId, withToken], async (response) => {
      return (await base.unpublishPost(newsPieceId)).next((successUnpublishPost) => {
        invalidateProjectNewsCache()

        return successUnpublishPost
      })
    })
  }

  async function updatePost(payload: api.CreateNews): Promise<api.HttpResult<api.News> | undefined> {
    if (newsPieceId === undefined) {
      return
    }

    return patch([newsPieceId, withToken], async (response) => {
      return (await base.updatePost(newsPieceId, payload)).next((successUpdatePost) => {
        invalidateProjectNewsCache()

        return successUpdatePost
      })
    })
  }

  async function deletePost(): Promise<api.HttpResult<void> | void> {
    if (newsPieceId) {
      const deletePostResponse = await base.deletePost(newsPieceId)

      return deletePostResponse.next((successDeletePost) => {
        invalidateProjectNewsCache()

        return successDeletePost
      })
    }
  }

  function getResponse(): hooks.Response<api.News> {
    if (newsPieceId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    const response = getCurrentResponse([newsPieceId, withToken])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response
  }

  function id(newsPieceID: number | undefined, withToken: boolean): string {
    return JSON.stringify([newsPieceID, withToken])
  }

  async function query(): Promise<void> {
    if (newsPieceId) {
      return queryNewsPiece(newsPieceId, withToken)
    }
  }
}
